import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { styles } from "./style";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Dialog } from "component/shared/dialog";
import { CustomAutocomplete } from "component/shared/auto-complete";
import { LeadSourceType } from "type/lead-source-type";
import { TriggerType } from "type/trigger-type";
import { EnrollmentModeType } from "type/enrollment-mode-type";
import { getCampaignRoutes } from "redux/selector";
import { getActiveDripCampaigns } from "redux/selector";
import { CampaignRoute } from "model/campaign-route";
import { createCampaignRoute, updateCampaignRoute } from "redux/slice/campaign-routes";
import { baseColors } from "theme";
interface OwnProps {
	open: boolean;
	edit?: boolean;
	campaignRoute?: CampaignRoute;
	onClose: () => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

interface CampaignOption {
	id: number;
	label: string;
}

const GROUP_ORDER = ['buyer', 'seller', 'subscriptions', 'other'] as const;

const UNMATCHED_SOURCE_TOOLTIP =
	"Catches leads that don't trigger any of your specific external source automations. " +
	"May not trigger if \"New lead from any external source\" is used in an active automation.";

const isSourceTrigger = (id: string, customSources: string[]): boolean =>
	id === TriggerType.UNMATCHED_LEAD_SOURCE.id ||
	!!LeadSourceType.getById(id) ||
	customSources.includes(id);

const AddRouteComponent: React.FC<Props> = ({ open, edit, campaignRoute, onClose, classes = {} }) => {
	const dispatch = useAppThunkDispatch();

	const campaignRoutes = useSelector(getCampaignRoutes);
	const campaigns = useSelector(getActiveDripCampaigns);

	const [selectedTriggers, setSelectedTriggers] = useState<string[]>([]);
	const [enrollmentMode, setEnrollmentMode] = useState<string>('ONE_TIME');
	const [reEngageDays, setReEngageDays] = useState<string>('365');
	const [customSources, setCustomSources] = useState<string[]>([]);
	const [customSourceInput, setCustomSourceInput] = useState<string>('');
	const [sourcesOpen, setSourcesOpen] = useState<boolean>(false);
	const [campaignInput, setCampaignInput] = useState<string>('');
	const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
	const [inputsUpdated, setInputsUpdated] = useState<boolean>(false);
	const [submitted, setSubmitted] = useState<boolean>(false);

	const knownCustomSources = useMemo(() => {
		const custom: string[] = [];
		campaignRoutes.forEach((route) => {
			route.triggers.forEach((id) => {
				if (!TriggerType.getById(id) && !LeadSourceType.getById(id) && !custom.includes(id)) {
					custom.push(id);
				}
			});
		});
		return custom;
	}, [campaignRoutes]);

	const allCustomSources = useMemo(() => {
		const all = new Set([...knownCustomSources, ...customSources]);
		return Array.from(all);
	}, [knownCustomSources, customSources]);

	const campaignOptions = useMemo<CampaignOption[]>(() => {
		return campaigns.map((c) => ({ id: c.id, label: c.name }));
	}, [campaigns]);

	const selectedCampaignOption = useMemo<CampaignOption | null>(() => {
		return campaignOptions.find((o) => o.id === campaignId) ?? null;
	}, [campaignOptions, campaignId]);

	const getCampaign = useCallback((id: number) => {
		return campaigns.find((c) => c.id === id);
	}, [campaigns]);

	const toggleTrigger = useCallback((id: string) => {
		setSelectedTriggers((prev) =>
			prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
		);
	}, []);

	const addCustomSource = useCallback(() => {
		const value = customSourceInput.trim();
		if (!value) return;
		setCustomSources((prev) => (prev.includes(value) ? prev : [...prev, value]));
		setSelectedTriggers((prev) => (prev.includes(value) ? prev : [...prev, value]));
		setCustomSourceInput('');
		setSourcesOpen(true);
	}, [customSourceInput]);

	const resetForm = useCallback((updated?: boolean) => {
		setSelectedTriggers([]);
		setEnrollmentMode('ONE_TIME');
		setReEngageDays('365');
		setCustomSources([]);
		setCustomSourceInput('');
		setSourcesOpen(false);
		setCampaignInput('');
		setCampaignId(undefined);
		setInputsUpdated(updated ? updated : false);
		setSubmitted(false);
	}, []);

	const setExistingRoutes = useCallback(() => {
		if (campaignRoute && !inputsUpdated) {
			setSelectedTriggers(campaignRoute.triggers);
			setEnrollmentMode(campaignRoute.enrollmentMode || 'ONE_TIME');
			setReEngageDays(campaignRoute.reEngageDays ? String(campaignRoute.reEngageDays) : '365');
			const routeCustomSources = campaignRoute.triggers.filter(
				(id) => !TriggerType.getById(id) && !LeadSourceType.getById(id)
			);
			setCustomSources(routeCustomSources);
			setCampaignInput(campaignRoute.campaign?.name || '');
			setCampaignId(campaignRoute.campaign?.id);
			setInputsUpdated(true);
			const hasSourceTrigger = campaignRoute.triggers.some(
				(id) => isSourceTrigger(id, routeCustomSources)
			);
			if (hasSourceTrigger) {
				setSourcesOpen(true);
			}
		}
		if (!campaignRoute && !inputsUpdated) {
			resetForm(true);
		}
	}, [campaignRoute, inputsUpdated, resetForm]);

	useEffect(() => {
		if (open) {
			setExistingRoutes();
		} else {
			resetForm();
		}
	}, [open, setExistingRoutes, resetForm]);

	const close = useCallback(() => {
		onClose();
		setSubmitted(false);
	}, [onClose]);

	const handleSave = useCallback(() => {
		setSubmitted(true);
		const reEngageDaysNum = parseInt(reEngageDays, 10);
		const reEngageDaysValid = enrollmentMode !== EnrollmentModeType.RE_ENGAGE.id
			|| (reEngageDays !== '' && /^\d+$/.test(reEngageDays) && reEngageDaysNum > 0);
		if (selectedTriggers.length === 0 || !campaignId || !reEngageDaysValid) return;
		const campaign = getCampaign(campaignId);
		if (!campaign) return;
		const reEngageDaysValue = enrollmentMode === EnrollmentModeType.RE_ENGAGE.id ? reEngageDaysNum : undefined;

		if (campaignRoute && campaignRoute.id) {
			dispatch(updateCampaignRoute({ campaignRoute: {
				id: campaignRoute.id,
				campaign,
				triggers: selectedTriggers,
				enrollmentMode,
				reEngageDays: reEngageDaysValue,
				inactive: campaignRoute.inactive,
			}}));
		} else {
			dispatch(createCampaignRoute({ campaignRoute: {
				campaign,
				triggers: selectedTriggers,
				enrollmentMode,
				reEngageDays: reEngageDaysValue,
				inactive: false,
			}}));
		}
		close();
	}, [selectedTriggers, enrollmentMode, reEngageDays, campaignId, getCampaign, campaignRoute, dispatch, close]);

	const renderChip = (id: string, label: string, tooltip?: React.ReactNode) => {
		const isSelected = selectedTriggers.includes(id);
		const text = isSelected ? `✓ ${label}` : label;
		const chipLabel = tooltip ? (
			<span className={classes.chipLabelWithTooltip}>
				{text}
				<Mui.Tooltip title={tooltip} placement="top" arrow>
					<span
						className={classes.chipTooltipIcon}
						onClick={(e) => e.stopPropagation()}
					>
						<Icons.Info size={14} />
					</span>
				</Mui.Tooltip>
			</span>
		) : text;
		return (
			<Mui.Chip
				key={id}
				label={chipLabel}
				onClick={() => toggleTrigger(id)}
				variant="outlined"
				className={isSelected ? classes.chipSelected : classes.chipUnselected}
			/>
		);
	};

	const renderGroupCard = (group: 'buyer' | 'seller' | 'subscriptions' | 'other') => {
		const triggers = TriggerType.byGroup()[group] || [];
		return (
			<div key={group} className={classes.groupCard}>
				<Mui.Typography variant="caption" className={classes.groupCardLabel}>
					{TriggerType.groupLabels[group]}
				</Mui.Typography>
				<div style={{ display: 'flex', flexWrap: 'wrap' }}>
					{triggers.map((t) => renderChip(t.id, t.label))}
				</div>
			</div>
		);
	};

	const triggerError = submitted && selectedTriggers.length === 0;
	const campaignError = submitted && !campaignId;
	const reEngageDaysError = submitted && enrollmentMode === EnrollmentModeType.RE_ENGAGE.id
		&& (reEngageDays === '' || !/^\d+$/.test(reEngageDays) || parseInt(reEngageDays, 10) <= 0);

	const newLeadTriggers = TriggerType.byGroup()['newLead'] || [];
	const formNewLeadTooltip = (
		<>
			Triggers the first time a website visitor becomes a new lead through any form or action on your
			site, such as a contact form or saved search signup. Existing leads will trigger different
			automations based on their activity.
		</>
	);
	const selectedSourceCount = selectedTriggers.filter(
		(id) => isSourceTrigger(id, allCustomSources)
	).length;

	return (
		<Dialog
			open={open}
			onClose={close}
			scroll="paper"
			fullWidth={true}
			maxWidth="md"
			classes={{ scrollPaper: classes.scrollPaper }}
		>
			<Mui.DialogTitle>
				{edit ? 'Edit Automation' : 'Add Automation'}
			</Mui.DialogTitle>
			<Mui.DialogContent>
				<Mui.Grid container direction="column" spacing={2}>

					{/* Trigger section */}
					<Mui.Grid item>
						<Mui.Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>
							Trigger automation based on...
						</Mui.Typography>
						<Mui.Button
							size="small"
							color="primary"
							style={{ padding: 0, minWidth: 0, marginBottom: 12, fontSize: 13 }}
							onClick={() => {
								const allIds = TriggerType.values().map((t) => t.id);
								const allSelected = allIds.every((id) => selectedTriggers.includes(id));
								setSelectedTriggers(allSelected ? [] : allIds);
							}}
						>
							Select all
						</Mui.Button>

						{/* New Lead card */}
						<div className={classes.groupCard}>
							<Mui.Typography variant="caption" className={classes.groupCardLabel}>
								{TriggerType.groupLabels['newLead']}
							</Mui.Typography>
							<div style={{ display: 'flex', flexWrap: 'wrap' }}>
								{newLeadTriggers.map((t) => renderChip(
									t.id,
									t.label,
									t.id === TriggerType.FORM_NEW_LEAD.id ? formNewLeadTooltip : undefined,
								))}
							</div>
							<Mui.Button
								size="small"
								color="primary"
								style={{ marginTop: 10, padding: 0, minWidth: 0, fontSize: 13 }}
								onClick={() => setSourcesOpen((o) => !o)}
							>
								Select specific external source{selectedSourceCount > 0 ? ` (${selectedSourceCount} selected)` : ''} {sourcesOpen ? '▼' : '▸'}
							</Mui.Button>
							<Mui.Collapse in={sourcesOpen}>
								<div style={{ marginTop: 8 }}>
									<div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 8 }}>
										{renderChip(TriggerType.UNMATCHED_LEAD_SOURCE.id, TriggerType.UNMATCHED_LEAD_SOURCE.label, UNMATCHED_SOURCE_TOOLTIP)}
									</div>
									<Mui.Divider style={{ marginBottom: 8 }} />
									<div style={{ display: 'flex', flexWrap: 'wrap' }}>
										{LeadSourceType.values().map((s) => renderChip(s.id, s.label))}
										{allCustomSources.map((id) => renderChip(id, id))}
									</div>
									<Mui.Typography variant="caption" style={{ display: 'block', marginTop: 10, marginBottom: 4, color: baseColors.teal600, fontWeight: 600 }}>
										Custom source
									</Mui.Typography>
									<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
										<Mui.TextField
											size="small"
											variant="outlined"
											placeholder="e.g. Home-Valuation"
											value={customSourceInput}
											onChange={(e) => setCustomSourceInput(e.target.value)}
											onKeyDown={(e) => { if (e.key === 'Enter') addCustomSource(); }}
											style={{ flex: 1 }}
											inputProps={{ style: { fontSize: 13, padding: '6px 10px' } }}
										/>
										<Mui.Button
											color="primary"
											onClick={addCustomSource}
											style={{ minWidth: 0, fontWeight: 600 }}
										>
											Add +
										</Mui.Button>
									</div>
								</div>
							</Mui.Collapse>
						</div>

						{/* Remaining group cards */}
						{GROUP_ORDER.map((group) => renderGroupCard(group))}

						{triggerError && (
							<Mui.Typography variant="caption" color="error" style={{ display: 'block', marginTop: 4 }}>
								Please select at least one activity.
							</Mui.Typography>
						)}
					</Mui.Grid>

					<Mui.Grid item><Mui.Divider /></Mui.Grid>

					{/* Campaign section */}
					<Mui.Grid item>
						<Mui.Typography variant="h6" style={{ fontWeight: 700, marginBottom: 12 }}>
							Then enroll lead in...
						</Mui.Typography>
						<CustomAutocomplete
							options={campaignOptions}
							textInputHeight={true}
							label="Campaign"
							optionLabelExtractor={(option) => option.label}
							margin="dense"
							placeholder="Select a campaign"
							onInputChange={(event, value) => {
								if (inputsUpdated) {
									setCampaignInput(value);
								}
							}}
							onChange={(event, option) => {
								if (option && option.id) {
									setCampaignId(option.id);
								}
							}}
							inputValue={campaignInput}
							value={selectedCampaignOption}
							helperText={campaignError ? 'Please choose a campaign.' : undefined}
							error={campaignError}
						/>
					</Mui.Grid>

					<Mui.Grid item><Mui.Divider /></Mui.Grid>

					{/* Enrollment mode */}
					<Mui.Grid item>
						<Mui.Typography variant="h6" style={{ fontWeight: 700, marginBottom: 12 }}>
							If lead has already completed campaign...
						</Mui.Typography>
						<Mui.RadioGroup
							value={enrollmentMode}
							onChange={(e) => setEnrollmentMode(e.target.value)}
						>
							{EnrollmentModeType.values().map((option) => (
								<Mui.FormControlLabel
									key={option.id}
									value={option.id}
									control={<Mui.Radio color="secondary" size="small" />}
									style={{ alignItems: 'flex-start', marginBottom: 8 }}
									label={
										<div style={{ paddingTop: 2 }}>
											<Mui.Typography variant="body2" style={{ fontWeight: 600 }}>
												{option.label}
											</Mui.Typography>
											<Mui.Typography variant="caption" color="textSecondary">
												{option.description}
											</Mui.Typography>
											{option.id === EnrollmentModeType.RE_ENGAGE.id && enrollmentMode === EnrollmentModeType.RE_ENGAGE.id && (
												<div style={{ marginTop: 8 }}>
													<Mui.Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
														Re-enroll after no activity for...
													</Mui.Typography>
													<Mui.TextField
														variant="outlined"
														size="small"
														value={reEngageDays}
														onChange={(e) => setReEngageDays(e.target.value)}
														error={reEngageDaysError}
														helperText={reEngageDaysError ? 'Please specify number of days.' : undefined}
														inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
														InputProps={{
															endAdornment: <Mui.InputAdornment position="end">days</Mui.InputAdornment>,
														}}
														style={{ width: 220 }}
													/>
												</div>
											)}
										</div>
									}
								/>
							))}
						</Mui.RadioGroup>
					</Mui.Grid>

				</Mui.Grid>
			</Mui.DialogContent>
			<Mui.DialogActions style={{ padding: '16px 24px' }}>
				<Mui.Button variant="outlined" color="primary" onClick={close}>
					Cancel
				</Mui.Button>
				<Mui.Button variant="contained" color="primary" onClick={handleSave}>
					{edit ? 'Save Changes' : 'Add Automation'}
				</Mui.Button>
			</Mui.DialogActions>
		</Dialog>
	);
};

export const AddRoute = withStyles(AddRouteComponent, styles);
